// ============================================================================
// FILE: ReportTab.jsx — Fixed version
// Fixes applied:
//  1. status field ("Paid"/"Waived") on assignments now respected
//  2. onStudentClick wired in PendingFeeReport rows
//  3. paymentHistory loaded via useEffect (not stale useMemo)
//  4. loadFromStorage memoized — no longer runs every render
//  5. Previous dues (previous_dues_<id>) included in outstanding balance
//  6. Dynamic years array derived from payment history
//  7. Campus-wise filtering — reports scope to a selected campus instead of
//     always aggregating every campus together
// ============================================================================
import { useState, useMemo, useEffect } from "react";
import { useAdminCampus } from "../../../utils/useAdminCampus";
import { alertDialog } from "../../../utils/dialogs";

// ── Constants ─────────────────────────────────────────────────────────────────
const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

const REPORT_TYPES = [
  { id: "dashboard",      label: "Dashboard Overview" },
  { id: "collection",     label: "Fee Collection" },
  { id: "classStatus",    label: "Class Fee Status" },
  { id: "pending",        label: "Pending Fees" },
  { id: "concession",     label: "Discounts" },
  { id: "classWise",      label: "Class-wise Analysis" },
  { id: "monthlyTrend",   label: "Monthly Trend" },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
// Letterhead identity. The campus is NOT hardcoded — it's derived from the
// students in the report (each Student carries `campus_name`, scoped by the
// backend to the logged-in admin's assigned campus).
const SCHOOL_NAME = "American Lycetuff School";
const SCHOOL_LOGO = "/als-logo.png"; // served from public/; resolved to an absolute URL when printing
const fmtPKR    = (n) => `PKR ${Number(n || 0).toLocaleString("en-PK")}`;
const rawNum    = (n) => Number(n || 0); // plain numeric value, used for CSV export so totals/sorting work in Excel
const formatDate = (d) => new Date(d).toLocaleDateString("en-PK", { day: "2-digit", month: "short", year: "numeric" });
const getGeneratedTimestamp = () => new Date().toLocaleString("en-PK", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
const formatCsvValue = (value) => String(value ?? "").replace(/"/g, '""');
const formatClassName = (c) => c ? c.replace(/Grade/i, "Class").trim() : "—";
const formatSection   = (s) => s ? s.replace(/Section\s*/i, "").trim() : "—";

// FIX 1: Respect assignment.status field.
// If an assignment is "Paid" or "Waived", count its netPayable as paid
// even if no matching payment_history record exists (covers the edit-modal path).
const getEffectivePaid = (assignment, paymentHistory) => {
  if (assignment.status === "Paid" || assignment.status === "Waived") {
    return assignment.netPayable || 0;
  }
  // Fall back to summing payment_history records for this student
  return paymentHistory
    .filter(p => Number(p.studentId) === assignment.studentId && p.feeId === assignment.entryKey)
    .reduce((s, p) => s + (p.amount || 0), 0);
};

// FIX 5: Load previous dues for a student from localStorage
const loadPreviousDues = (studentId) => {
  try {
    const dues = JSON.parse(localStorage.getItem(`previous_dues_${studentId}`) || "[]");
    return dues.filter(d => !d.paid).reduce((s, d) => s + (d.amount || 0), 0);
  } catch { return 0; }
};

// Build a complete student financial summary respecting status + previous dues
const buildStudentSummary = (student, flatAssignments, paymentHistory) => {
  const assignments = flatAssignments.filter(a => a.studentId === student.id);

  const totalFee = assignments.reduce((sum, a) => sum + (a.netPayable || 0), 0);

  // Payment history total (for partial payments recorded via payment_history)
  const historyPaid = paymentHistory
    .filter(p => Number(p.studentId) === student.id)
    .reduce((sum, p) => sum + (p.amount || 0), 0);

  // Respect status flags on individual assignments
  const statusPaid = assignments
    .filter(a => a.status === "Paid" || a.status === "Waived")
    .reduce((sum, a) => sum + (a.netPayable || 0), 0);

  // Use whichever is larger: explicit payment records or status-marked amounts
  const totalPaid = Math.max(historyPaid, statusPaid);

  // FIX 5: Include previous dues in outstanding balance
  const previousDues = loadPreviousDues(student.id);
  const balance = totalFee - totalPaid + previousDues;

  const status = totalPaid >= totalFee && totalFee > 0 ? "paid"
    : totalPaid > 0 ? "partial"
    : "pending";

  return { student, totalFee, totalPaid, balance, previousDues, status, assignments };
};

// FIX 4: Load from storage only once (called inside useMemo in main component)
const loadFromStorage = () => {
  try {
    const students       = JSON.parse(localStorage.getItem("students") || "[]");
    const feeAssignments = JSON.parse(localStorage.getItem("fee_assignments_v2") || "{}");
    const paymentHistory = JSON.parse(localStorage.getItem("payment_history") || "[]");
    return { students, feeAssignments, paymentHistory };
  } catch { return { students: [], feeAssignments: {}, paymentHistory: [] }; }
};

// FIX 7: normalize a campus label off whatever field the backend used
const normalizeStudent = (student = {}) => ({
  ...student,
  class: student.class || student.className || student.class_name || "",
  section: student.section || student.sectionName || student.section_name || student.class_section || "",
  rollNo: student.rollNo || student.student_id || student.id,
  campus: student.campus || student.campus_name || student.campusName || student.Campus || "",
  campusId: student.campusId ?? student.campus_id ?? null,
});

const normalizeStudents = (students = []) =>
  Array.isArray(students) ? students.map(normalizeStudent) : [];

// Flatten fee_assignments_v2: { studentId: { entryKey: {...} } } → flat list
// Also attach entryKey so getEffectivePaid can match by feeId
const flattenAssignments = (feeAssignments) => {
  const flat = [];
  Object.entries(feeAssignments).forEach(([studentId, categories]) => {
    if (typeof categories === "object" && categories !== null) {
      Object.entries(categories).forEach(([entryKey, assignment]) => {
        flat.push({
          studentId: parseInt(studentId),
          entryKey,
          categoryId: assignment.categoryId || entryKey,
          ...assignment,
        });
      });
    }
  });
  return flat;
};

// FIX 6: Build dynamic years array from payment history dates
const deriveYears = (paymentHistory) => {
  const currentYear = new Date().getFullYear();
  const yearsFromHistory = paymentHistory
    .map(p => new Date(p.date).getFullYear())
    .filter(y => !isNaN(y));
  const allYears = new Set([currentYear - 1, currentYear, currentYear + 1, ...yearsFromHistory]);
  return Array.from(allYears).sort((a, b) => a - b);
};

// ── Academic Session helpers ──────────────────────────────────────────────────
// Reads the session straight off the assignment record (e.g. a.session).
// This component doesn't infer it from month/date — it's expected that the
// fee assignment data itself carries this field, wired up wherever it
// gets set/assigned.
const deriveSessions = (flatAssignments) => {
  const unique = [...new Set(flatAssignments.map(a => a.session).filter(Boolean))];
  return unique.sort((a, b) => String(b).localeCompare(String(a)));
};

// FIX 7: unique, sorted list of campuses present in the student roster
const deriveCampuses = (students) => {
  const unique = [...new Set(students.map(s => s.campus).filter(Boolean))];
  return unique.sort((a, b) => String(a).localeCompare(String(b)));
};

// ── Palette + ledger helpers (shared with the Fee modals' ledger language) ─────
const C = {
  navy:"#0f1d36", navyMid:"#1c3461", blue:"#2a4a7f", blueSoft:"rgba(30,58,95,0.10)",
  green:"#2e7d32", greenSoft:"rgba(46,125,50,0.10)",
  red:"#c41e24", redSoft:"rgba(196,30,36,0.08)",
  amber:"#8a6c1f", amberSoft:"rgba(196,154,42,0.12)",
  slate:"#5a5e6a", slateLight:"#8e92a0", border:"#e8e9ed", bg:"#f8f9fc",
  white:"#ffffff", text:"#1a1d23", parchment:"#fbf8f2", ledgerLine:"#ddd3bd",
};
const mono = { fontFamily:"'IBM Plex Mono', monospace", fontVariantNumeric:"tabular-nums" };

// ── Icons (24×24 stroke, Lucide-style) ─────────────────────────────────────────
const ArrowLeft = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
  </svg>
);
const svg = (children) => ({ size = 16 } = {}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);
const Ico = {
  dashboard:    svg(<><rect x="3" y="3" width="7" height="9"/><rect x="14" y="3" width="7" height="5"/><rect x="14" y="12" width="7" height="9"/><rect x="3" y="16" width="7" height="5"/></>),
  collection:   svg(<><rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="12" cy="12" r="2.5"/><path d="M6 12h.01M18 12h.01"/></>),
  pending:      svg(<><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></>),
  concession:   svg(<><path d="M9 9h.01M15 15h.01M16 8l-8 8"/><path d="M3 7v4a2 2 0 0 0 .59 1.42l7.99 7.99a2 2 0 0 0 2.83 0l4.17-4.17a2 2 0 0 0 0-2.83l-7.99-7.99A2 2 0 0 0 11 5H5a2 2 0 0 0-2 2z"/></>),
  classWise:    svg(<><path d="M3 3v18h18"/><rect x="7" y="12" width="3" height="6"/><rect x="12" y="8" width="3" height="10"/><rect x="17" y="4" width="3" height="14"/></>),
  monthlyTrend: svg(<><path d="M3 17l6-6 4 4 8-8"/><path d="M17 7h4v4"/></>),
  roster:       svg(<><rect x="4" y="3" width="16" height="18" rx="2"/><path d="M9 8h6M9 12h6M9 16h3"/><path d="M6.5 8h.01M6.5 12h.01M6.5 16h.01"/></>),
  download:     svg(<><path d="M12 3v12m0 0l-4-4m4 4l4-4"/><path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"/></>),
  printer:      svg(<><path d="M6 9V3h12v6"/><rect x="6" y="14" width="12" height="7" rx="1"/><path d="M6 18H4a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-2"/></>),
};
const REPORT_ICONS = {
  dashboard: Ico.dashboard, collection: Ico.collection, classStatus: Ico.roster,
  pending: Ico.pending, concession: Ico.concession, classWise: Ico.classWise,
  monthlyTrend: Ico.monthlyTrend,
};

// ── Shared UI Primitives ──────────────────────────────────────────────────────
const S = {
  wrap: {
    padding: "4px 0 32px",
    fontFamily: "'Inter','Segoe UI',sans-serif",
  },

  card: {
    background: C.white,
    borderRadius: "12px",
    border: `1px solid ${C.ledgerLine}`,
    marginBottom: "20px",
    overflow: "hidden",
    boxShadow: "0 1px 4px rgba(15,29,54,0.06)",
  },

  cardHead: {
    padding: "15px 20px",
    background: C.navy,
    borderBottom: `3px solid ${C.amber}`,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },

  cardTitle: {
    fontFamily: "'Playfair Display', Georgia, serif",
    fontSize: "15px",
    fontWeight: "700",
    color: "#fff",
    letterSpacing: "0.2px",
  },

  filterRow: {
    padding: "16px 20px",
    display: "flex",
    gap: "14px",
    flexWrap: "wrap",
    alignItems: "flex-end",
    borderBottom: `1px dashed ${C.ledgerLine}`,
    background: C.parchment,
  },

  fg: {
    display: "flex",
    flexDirection: "column",
    gap: "5px",
    minWidth: "140px",
  },

  label: {
    fontSize: "11px",
    fontWeight: "700",
    color: C.slate,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },

  select: {
    padding: "9px 12px",
    borderRadius: "8px",
    border: `1.5px solid ${C.border}`,
    fontSize: "13px",
    background: "#fff",
    cursor: "pointer",
    color: C.text,
    outline: "none",
  },

  input: {
    padding: "9px 12px",
    borderRadius: "8px",
    border: `1.5px solid ${C.border}`,
    fontSize: "13px",
    background: "#fff",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
    background: C.white,
  },

  th: {
    textAlign: "left",
    padding: "10px 16px",
    fontSize: "10.5px",
    fontWeight: "700",
    color: C.amber,
    background: C.amberSoft,
    borderBottom: `2px solid ${C.amber}`,
    textTransform: "uppercase",
    letterSpacing: "0.6px",
    whiteSpace: "nowrap",
  },

  td: {
    padding: "12px 16px",
    fontSize: "13px",
    color: C.text,
    borderBottom: `1px solid ${C.ledgerLine}`,
    verticalAlign: "middle",
  },

  tdNum: {
    padding: "12px 16px",
    fontSize: "13px",
    color: C.text,
    borderBottom: `1px solid ${C.ledgerLine}`,
    verticalAlign: "middle",
    textAlign: "right",
    fontFamily: "'IBM Plex Mono', monospace",
    fontVariantNumeric: "tabular-nums",
  },

  badge: (bg, color) => ({
    display: "inline-block",
    padding: "3px 10px",
    borderRadius: "20px",
    fontSize: "11px",
    fontWeight: "700",
    background: bg,
    color,
  }),

  btn: (bg, color, border) => ({
    padding: "9px 16px",
    borderRadius: "8px",
    border: border || "none",
    background: bg,
    color,
    fontSize: "12.5px",
    fontWeight: "600",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "7px",
    whiteSpace: "nowrap",
    transition: "opacity .15s",
  }),

  empty: {
    textAlign: "center",
    padding: "56px 20px",
    color: C.slate,
    fontSize: "13px",
  },

  statGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit,minmax(210px,1fr))",
    gap: "16px",
    marginBottom: "20px",
  },
};

// ── Report navigator — the signature element. A segmented, icon-led selector
//    that replaces the old plain <select>; the active tab is "stamped" navy
//    with a gold icon, echoing the fee challan's ink-and-seal language. ───────
const ReportTypeTabs = ({ value, onChange }) => (
  <div style={{ display:"flex", gap:"8px", padding:"14px 20px", overflowX:"auto", borderBottom:`1px dashed ${C.ledgerLine}`, background:C.parchment }}>
    {REPORT_TYPES.map(t => {
      const active = value === t.id;
      const Icn = REPORT_ICONS[t.id];
      return (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          style={{
            display:"flex", alignItems:"center", gap:"8px", padding:"9px 14px", borderRadius:"9px",
            border:`1.5px solid ${active ? C.navy : C.ledgerLine}`,
            background: active ? C.navy : C.white, color: active ? "#fff" : C.slate,
            fontSize:"12.5px", fontWeight:"700", cursor:"pointer", whiteSpace:"nowrap",
            boxShadow: active ? "0 4px 14px rgba(15,29,54,0.20)" : "none",
            transition:"border-color .15s, color .15s, background .15s",
          }}
          onMouseEnter={e => { if (!active) { e.currentTarget.style.borderColor = C.navy; e.currentTarget.style.color = C.navy; } }}
          onMouseLeave={e => { if (!active) { e.currentTarget.style.borderColor = C.ledgerLine; e.currentTarget.style.color = C.slate; } }}
        >
          <span style={{ display:"flex", color: active ? C.amber : C.slateLight }}><Icn /></span>
          {t.label}
        </button>
      );
    })}
  </div>
);

// ── Stat Card — a stamped ledger figure: accent top-rule, mono numerals ────────
const StatCard = ({ label, value, sub, accent = C.blue }) => (
  <div style={{ background:C.white, border:`1px solid ${C.ledgerLine}`, borderRadius:"10px", padding:"16px 18px", borderTop:`3px solid ${accent}`, boxShadow:"0 1px 3px rgba(15,29,54,0.05)" }}>
    <div style={{ fontSize:"10.5px", fontWeight:"700", color:C.slate, textTransform:"uppercase", letterSpacing:"0.6px", marginBottom:"8px" }}>{label}</div>
    <div style={{ ...mono, fontSize:"22px", fontWeight:"700", color:C.text, lineHeight:1.1, marginBottom: sub ? "4px" : 0 }}>{value}</div>
    {sub && <div style={{ fontSize:"11px", color:C.slateLight }}>{sub}</div>}
  </div>
);

// ── Progress Bar ──────────────────────────────────────────────────────────────
const ProgressBar = ({ pct, color = C.blue }) => (
  <div style={{ height:"7px", background:"rgba(221,211,189,0.5)", borderRadius:"4px", overflow:"hidden", marginTop:"4px" }}>
    <div style={{ height:"100%", width:`${Math.min(pct,100)}%`, background:color, borderRadius:"4px", transition:"width 0.4s ease" }} />
  </div>
);

// ── Dashboard Overview ────────────────────────────────────────────────────────
const DashboardOverview = ({ students, flatAssignments, paymentHistory }) => {
  const stats = useMemo(() => {
    const totalStudents = students.length;
    const studentsWithFees = new Set(flatAssignments.map(a => a.studentId)).size;
    const totalMonthlyReceivable = flatAssignments.reduce((s, a) => s + (a.netPayable || 0), 0);
    const totalConcessions = flatAssignments.reduce((s, a) => s + (a.concessionAmount || 0), 0);

    // FIX 1: Use max of history-paid vs status-paid per student
    const totalCollected = students.reduce((sum, student) => {
      const summary = buildStudentSummary(student, flatAssignments, paymentHistory);
      return sum + summary.totalPaid;
    }, 0);

    const collectionRate = totalMonthlyReceivable > 0
      ? Math.min(((totalCollected / totalMonthlyReceivable) * 100), 100).toFixed(1) : 0;
    return { totalStudents, studentsWithFees, totalMonthlyReceivable, totalConcessions, totalCollected, collectionRate };
  }, [students, flatAssignments, paymentHistory]);

  const monthlyCollected = useMemo(() => {
    const map = {};
    paymentHistory.forEach(p => {
      const d = new Date(p.date);
      const key = d.getMonth();
      map[key] = (map[key] || 0) + (p.amount || 0);
    });
    return map;
  }, [paymentHistory]);

  const maxMonthly = Math.max(...Object.values(monthlyCollected), 1) || 1;

  return (
    <>
      <div style={S.statGrid}>
        <StatCard label="Total Students" value={stats.totalStudents} sub={`${stats.studentsWithFees} with fees assigned`} accent="#2a4a7f" />
        <StatCard label="Monthly Receivable" value={fmtPKR(stats.totalMonthlyReceivable)} sub="across all assignments" accent="#2e7d32" />
        <StatCard label="Total Collected" value={fmtPKR(stats.totalCollected)} sub={`${stats.collectionRate}% collection rate`} accent="#3d6098" />
        <StatCard label="Total Discounts" value={fmtPKR(stats.totalConcessions)} sub="granted to students" accent="#8a6c1f" />
      </div>

      <div style={{ ...S.card, marginTop: 0 }}>
        <div style={S.cardHead}>
          <span style={S.cardTitle}>Collection by Month</span>
          <span style={{ fontSize:"11px", color:"rgba(255,255,255,0.6)" }}>from payment records</span>
        </div>
        <div style={{ padding:"20px", background:C.parchment }}>
          {MONTHS.map((month, idx) => {
            const amt = monthlyCollected[idx] || 0;
            const pct = (amt / maxMonthly) * 100;
            const isPeak = amt > 0 && amt === maxMonthly;
            return (
              <div key={idx} style={{ display:"flex", alignItems:"center", gap:"12px", marginBottom:"9px" }}>
                <div style={{ width:"82px", fontSize:"11.5px", fontWeight:"600", color:C.slate, textTransform:"uppercase", letterSpacing:"0.4px" }}>{month.slice(0,3)}</div>
                <div style={{ flex:1, background:"rgba(221,211,189,0.45)", borderRadius:"6px", height:"26px", overflow:"hidden" }}>
                  <div style={{ height:"26px", background: isPeak ? `linear-gradient(90deg,#8a6c1f,#a8842a)` : `linear-gradient(90deg,#1c3461,#2a4a7f)`, borderRadius:"6px", width:`${Math.max(pct,1.5)}%`, display:"flex", alignItems:"center", justifyContent:"flex-end", paddingRight:"9px", transition:"width 0.5s ease", boxShadow: isPeak ? "0 0 0 1px rgba(138,108,31,0.4)" : "none" }}>
                    {amt > 0 && <span style={{ ...mono, color:"#fff", fontSize:"11px", fontWeight:"700", whiteSpace:"nowrap" }}>{fmtPKR(amt)}</span>}
                  </div>
                </div>
                {amt === 0 && <span style={{ fontSize:"11px", color:C.slateLight, fontStyle:"italic", width:"70px" }}>No records</span>}
                {isPeak && <span style={{ fontSize:"9px", fontWeight:"700", color:C.amber, textTransform:"uppercase", letterSpacing:"0.6px", whiteSpace:"nowrap" }}>● Peak</span>}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

// ── Fee Collection Report ─────────────────────────────────────────────────────
const CollectionReport = ({ students, flatAssignments, paymentHistory, showCampus }) => {
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear,  setSelectedYear]  = useState(new Date().getFullYear());

  // FIX 6: dynamic years
  const years = useMemo(() => deriveYears(paymentHistory), [paymentHistory]);

  const data = useMemo(() => {
    return students.map(student => {
      const assignments = flatAssignments.filter(a => a.studentId === student.id);
      const totalFee = assignments.reduce((s, a) => s + (a.netPayable || 0), 0);

      // Payments recorded for this month/year
      const payments = paymentHistory.filter(p => {
        const d = new Date(p.date);
        return Number(p.studentId) === student.id
          && d.getMonth() === selectedMonth
          && d.getFullYear() === selectedYear;
      });
      const historyPaid = payments.reduce((s, p) => s + (p.amount || 0), 0);

      // FIX 1: Also check status-marked assignments for this month
      const monthStr = `${selectedYear}-${String(selectedMonth + 1).padStart(2, "0")}`;
      const statusPaid = assignments
        .filter(a => (a.month === monthStr) && (a.status === "Paid" || a.status === "Waived"))
        .reduce((s, a) => s + (a.netPayable || 0), 0);

      const totalPaid = Math.max(historyPaid, statusPaid);

      return {
        ...student, totalFee, totalPaid, payments,
        status: totalPaid >= totalFee && totalFee > 0 ? "paid" : totalPaid > 0 ? "partial" : "pending"
      };
    }).filter(s => s.totalFee > 0);
  }, [students, flatAssignments, paymentHistory, selectedMonth, selectedYear]);

  const totalExpected  = data.reduce((s, d) => s + d.totalFee, 0);
  const totalCollected = data.reduce((s, d) => s + d.totalPaid, 0);
  const rate = totalExpected > 0 ? ((totalCollected / totalExpected) * 100).toFixed(1) : 0;

  return (
    <div>
      <div style={S.statGrid}>
        <StatCard label="Expected" value={fmtPKR(totalExpected)} accent="#2a4a7f" />
        <StatCard label="Collected" value={fmtPKR(totalCollected)} accent="#2e7d32" />
        <StatCard label="Pending" value={fmtPKR(totalExpected - totalCollected)} accent="#d4272e" />
        <StatCard label="Collection Rate" value={`${rate}%`} accent="#3d6098" />
      </div>

      <div style={S.card}>
        <div style={S.filterRow}>
          <div style={S.fg}>
            <label style={S.label}>Month</label>
            <select style={S.select} value={selectedMonth} onChange={e => setSelectedMonth(parseInt(e.target.value))}>
              {MONTHS.map((m, i) => <option key={i} value={i}>{m}</option>)}
            </select>
          </div>
          <div style={S.fg}>
            <label style={S.label}>Year</label>
            <select style={S.select} value={selectedYear} onChange={e => setSelectedYear(parseInt(e.target.value))}>
              {years.map(y => <option key={y}>{y}</option>)}
            </select>
          </div>
        </div>
        <div style={{ overflowX:"auto" }}>
          <table style={S.table}>
            <thead>
              <tr>
                {["#","Student", ...(showCampus ? ["Campus"] : []), "Class","Section","Roll No.","Fee Due","Paid","Balance","Status"].map(h => (
                  <th key={h} style={["Fee Due","Paid","Balance"].includes(h) ? { ...S.th, textAlign:"right" } : S.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.length === 0 ? (
                <tr><td colSpan={showCampus ? 10 : 9} style={S.empty}>No fee assignments found for this period</td></tr>
              ) : data.map((s, i) => (
                <tr key={s.id} style={{ background: i % 2 === 0 ? "#fff" : "#f8f9fc" }}>
                  <td style={{ ...S.td, color:"#8e92a0", fontWeight:600 }}>{i+1}</td>
                  <td style={{ ...S.td, fontWeight:700 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:"8px" }}>
                      <div style={{ width:28, height:28, borderRadius:"50%", background:"#0f1d36", color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:700, fontSize:11, flexShrink:0 }}>
                        {s.name.charAt(0).toUpperCase()}
                      </div>
                      {s.name}
                    </div>
                  </td>
                  {showCampus && <td style={S.td}><span style={S.badge("rgba(138,108,31,0.12)","#8a6c1f")}>{s.campus || "—"}</span></td>}
                  <td style={S.td}><span style={S.badge("rgba(30,58,95,0.10)","#2a4a7f")}>{formatClassName(s.class)}</span></td>
                  <td style={S.td}>{formatSection(s.section)}</td>
                  <td style={{ ...S.td, ...mono, fontWeight:600 }}>{s.rollNo}</td>
                  <td style={S.tdNum}>{fmtPKR(s.totalFee)}</td>
                  <td style={{ ...S.tdNum, color:"#2e7d32", fontWeight:700 }}>{fmtPKR(s.totalPaid)}</td>
                  <td style={{ ...S.tdNum, color: s.totalFee - s.totalPaid > 0 ? "#c41e24" : "#2e7d32", fontWeight:700 }}>{fmtPKR(s.totalFee - s.totalPaid)}</td>
                  <td style={S.td}>
                    <span style={S.badge(
                      s.status === "paid" ? "rgba(46,125,50,0.12)" : s.status === "partial" ? "rgba(196,154,42,0.15)" : "rgba(196,30,36,0.1)",
                      s.status === "paid" ? "#2e7d32" : s.status === "partial" ? "#8a6c1f" : "#c41e24"
                    )}>
                      {s.status === "paid" ? "Paid" : s.status === "partial" ? "Partial" : "Pending"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ── Pending Fees ──────────────────────────────────────────────────────────────
// FIX 2: onStudentClick is now wired to student name cell
const PendingFeeReport = ({ students, flatAssignments, paymentHistory, onStudentClick, showCampus }) => {
  const data = useMemo(() => {
    return students.map(student => {
      const summary = buildStudentSummary(student, flatAssignments, paymentHistory);
      return summary;
    // FIX 5: balance now includes previous dues, so this correctly filters students with real outstanding
    }).filter(s => s.balance > 0).sort((a, b) => b.balance - a.balance);
  }, [students, flatAssignments, paymentHistory]);

  const totalPending = data.reduce((s, d) => s + d.balance, 0);

  return (
    <div>
      <div style={S.statGrid}>
        <StatCard label="Students with Dues" value={data.length} accent="#d4272e" />
        <StatCard label="Total Outstanding" value={fmtPKR(totalPending)} accent="#c41e24" />
        <StatCard label="Avg Due per Student" value={fmtPKR(data.length ? totalPending / data.length : 0)} accent="#8a6c1f" />
      </div>

      <div style={S.card}>
        <div style={S.cardHead}><span style={S.cardTitle}>Outstanding Balances</span></div>
        <div style={{ overflowX:"auto" }}>
          <table style={S.table}>
            <thead>
              <tr>
                {["#","Student", ...(showCampus ? ["Campus"] : []), "Class","Section","Roll No.","Total Fee","Paid","Prev. Dues","Outstanding"].map(h => <th key={h} style={["Total Fee","Paid","Prev. Dues","Outstanding"].includes(h) ? { ...S.th, textAlign:"right" } : S.th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.length === 0 ? (
                <tr><td colSpan={showCampus ? 10 : 9} style={S.empty}>🎉 No pending fees — all students are up to date!</td></tr>
              ) : data.map(({ student: s, totalFee, totalPaid, balance, previousDues }, i) => (
                <tr key={s.id} style={{ background: i % 2 === 0 ? "#fff" : "#f8f9fc" }}>
                  <td style={{ ...S.td, color:"#8e92a0", fontWeight:600 }}>{i+1}</td>
                  {/* FIX 2: student name is now clickable */}
                  <td
                    style={{ ...S.td, fontWeight:700, cursor:"pointer" }}
                    onClick={() => onStudentClick && onStudentClick(s.id)}
                  >
                    <div style={{ display:"flex", alignItems:"center", gap:"8px" }}>
                      <div style={{ width:28, height:28, borderRadius:"50%", background:"#c41e24", color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:700, fontSize:11, flexShrink:0 }}>
                        {s.name.charAt(0).toUpperCase()}
                      </div>
                      <span style={{ textDecoration:"underline", textDecorationStyle:"dotted", color:"#2a4a7f" }}>{s.name}</span>
                    </div>
                  </td>
                  {showCampus && <td style={S.td}><span style={S.badge("rgba(138,108,31,0.12)","#8a6c1f")}>{s.campus || "—"}</span></td>}
                  <td style={S.td}><span style={S.badge("rgba(30,58,95,0.10)","#2a4a7f")}>{formatClassName(s.class)}</span></td>
                  <td style={S.td}>{formatSection(s.section)}</td>
                  <td style={{ ...S.td, ...mono, fontWeight:600 }}>{s.rollNo}</td>
                  <td style={S.tdNum}>{fmtPKR(totalFee)}</td>
                  <td style={{ ...S.tdNum, color:"#2e7d32", fontWeight:700 }}>{fmtPKR(totalPaid)}</td>
                  <td style={{ ...S.tdNum, color: previousDues > 0 ? "#8a6c1f" : "#8e92a0", fontWeight:600 }}>
                    {previousDues > 0 ? fmtPKR(previousDues) : "—"}
                  </td>
                  <td style={{ ...S.tdNum }}>
                    <span style={{ ...S.badge("rgba(196,30,36,0.1)","#c41e24"), ...mono }}>{fmtPKR(balance)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ── Concession Report ─────────────────────────────────────────────────────────
const ConcessionReport = ({ students, flatAssignments, showCampus }) => {
  const data = useMemo(() => {
    return flatAssignments
      .filter(a => a.concessionAmount > 0)
      .map(a => {
        const student = students.find(s => s.id === a.studentId);
        return student ? { ...student, ...a } : null;
      })
      .filter(Boolean);
  }, [students, flatAssignments]);

  const totalConcessions = data.reduce((s, d) => s + (d.concessionAmount || 0), 0);

  return (
    <div>
      <div style={S.statGrid}>
        <StatCard label="Students with Discounts" value={data.length} accent="#8a6c1f" />
        <StatCard label="Total Discounts" value={fmtPKR(totalConcessions)} accent="#8a6c1f" />
        <StatCard label="Avg Discount" value={fmtPKR(data.length ? totalConcessions / data.length : 0)} accent="#3d6098" />
      </div>
      <div style={S.card}>
        <div style={S.cardHead}><span style={S.cardTitle}>Discount Details</span></div>
        <div style={{ overflowX:"auto" }}>
          <table style={S.table}>
            <thead>
              <tr>
                {["#","Student", ...(showCampus ? ["Campus"] : []), "Class","Fee Category","Original","Discount","Net Payable","Reason"].map(h => <th key={h} style={["Original","Discount","Net Payable"].includes(h) ? { ...S.th, textAlign:"right" } : S.th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.length === 0 ? (
                <tr><td colSpan={showCampus ? 9 : 8} style={S.empty}>No concessions have been applied</td></tr>
              ) : data.map((d, i) => (
                <tr key={`${d.id}-${d.categoryId}`} style={{ background: i % 2 === 0 ? "#fff" : "#f8f9fc" }}>
                  <td style={{ ...S.td, color:"#8e92a0", fontWeight:600 }}>{i+1}</td>
                  <td style={{ ...S.td, fontWeight:700 }}>{d.name}</td>
                  {showCampus && <td style={S.td}><span style={S.badge("rgba(138,108,31,0.12)","#8a6c1f")}>{d.campus || "—"}</span></td>}
                  <td style={S.td}><span style={S.badge("rgba(30,58,95,0.10)","#2a4a7f")}>{formatClassName(d.class)}</span></td>
                  <td style={S.td}><span style={S.badge(C.amberSoft, C.amber)}>{d.categoryLabel}</span></td>
                  <td style={S.tdNum}>{fmtPKR(d.subtotal)}</td>
                  <td style={{ ...S.tdNum, color:"#2e7d32", fontWeight:700 }}>−{fmtPKR(d.concessionAmount)}</td>
                  <td style={{ ...S.tdNum, fontWeight:700 }}>{fmtPKR(d.netPayable)}</td>
                  <td style={{ ...S.td, color:"#5a5e6a", fontSize:"12px" }}>
                    {d.concession?.reason || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ── Class-wise Analysis ───────────────────────────────────────────────────────
const ClassWiseAnalysis = ({ students, flatAssignments, paymentHistory }) => {
  const data = useMemo(() => {
    const map = new Map();
    students.forEach(s => {
      if (!map.has(s.class)) map.set(s.class, { className: s.class, students: 0, totalFee: 0, totalPaid: 0, concessions: 0 });
      const info = map.get(s.class);
      info.students++;
      const assignments = flatAssignments.filter(a => a.studentId === s.id);
      info.totalFee += assignments.reduce((sum, a) => sum + (a.netPayable || 0), 0);
      info.concessions += assignments.reduce((sum, a) => sum + (a.concessionAmount || 0), 0);

      // FIX 1: use buildStudentSummary for accurate paid amount
      const summary = buildStudentSummary(s, flatAssignments, paymentHistory);
      info.totalPaid += summary.totalPaid;
    });
    return Array.from(map.values())
      .map(c => ({ ...c, balance: c.totalFee - c.totalPaid, rate: c.totalFee > 0 ? Math.min((c.totalPaid / c.totalFee) * 100, 100) : 0 }))
      .sort((a, b) => b.totalFee - a.totalFee);
  }, [students, flatAssignments, paymentHistory]);

  return (
    <div>
      <div style={S.statGrid}>
        <StatCard label="Total Classes" value={data.length} accent="#2a4a7f" />
        <StatCard label="Total Receivable" value={fmtPKR(data.reduce((s,d) => s+d.totalFee,0))} accent="#2e7d32" />
        <StatCard label="Total Collected" value={fmtPKR(data.reduce((s,d) => s+d.totalPaid,0))} accent="#3d6098" />
      </div>
      <div style={S.card}>
        <div style={S.cardHead}><span style={S.cardTitle}>Class-wise Breakdown</span></div>
        <div style={{ overflowX:"auto" }}>
          <table style={S.table}>
            <thead>
              <tr>
                {["Class","Students","Total Fee","Collected","Balance","Discounts","Collection Rate"].map(h => <th key={h} style={["Students","Total Fee","Collected","Balance","Discounts"].includes(h) ? { ...S.th, textAlign:"right" } : S.th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.length === 0 ? (
                <tr><td colSpan="7" style={S.empty}>No class data available</td></tr>
              ) : data.map((c, i) => (
                <tr key={c.className} style={{ background: i % 2 === 0 ? "#fff" : "#f8f9fc" }}>
                  <td style={{ ...S.td, fontWeight:700 }}><span style={S.badge("rgba(30,58,95,0.10)","#2a4a7f")}>{formatClassName(c.className)}</span></td>
                  <td style={S.tdNum}>{c.students}</td>
                  <td style={S.tdNum}>{fmtPKR(c.totalFee)}</td>
                  <td style={{ ...S.tdNum, color:"#2e7d32", fontWeight:700 }}>{fmtPKR(c.totalPaid)}</td>
                  <td style={{ ...S.tdNum, color: c.balance > 0 ? "#c41e24" : "#2e7d32", fontWeight:700 }}>{fmtPKR(c.balance)}</td>
                  <td style={S.tdNum}>{fmtPKR(c.concessions)}</td>
                  <td style={{ ...S.td, minWidth:"150px" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:"8px" }}>
                      <span style={{ fontSize:"12px", fontWeight:"600", width:"36px" }}>{c.rate.toFixed(0)}%</span>
                      <div style={{ flex:1 }}><ProgressBar pct={c.rate} color={c.rate >= 80 ? "#2e7d32" : c.rate >= 50 ? "#8a6c1f" : "#c41e24"} /></div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ── Class Fee Status (per-student roster: who paid, on what date, who hasn't) ──
const ClassFeeStatusReport = ({ students, flatAssignments, paymentHistory, classFilter, showCampus }) => {
  const rows = useMemo(() => {
    return students.map(student => {
      const summary = buildStudentSummary(student, flatAssignments, paymentHistory);
      const pays = paymentHistory
        .filter(p => Number(p.studentId) === student.id)
        .sort((a, b) => new Date(b.date) - new Date(a.date));
      // Latest payment date — from a recorded payment, or a status-marked assignment
      const lastPaid = pays[0]?.date
        || summary.assignments.find(a => (a.status === "Paid" || a.status === "Waived") && a.paymentDate)?.paymentDate
        || null;
      return { ...summary, lastPaid, payCount: pays.length };
    }).sort((a, b) => a.student.name.localeCompare(b.student.name));
  }, [students, flatAssignments, paymentHistory]);

  const paidCount    = rows.filter(r => r.status === "paid").length;
  const partialCount = rows.filter(r => r.status === "partial").length;
  const unpaidCount  = rows.filter(r => r.status === "pending").length;
  const totalCollected = rows.reduce((s, r) => s + r.totalPaid, 0);

  return (
    <div>
      <div style={S.statGrid}>
        <StatCard label="Fully Paid"  value={paidCount}    sub={`of ${rows.length} students`} accent={C.green} />
        <StatCard label="Partial"     value={partialCount} sub="some balance left"            accent={C.amber} />
        <StatCard label="Not Paid"    value={unpaidCount}  sub="no payment recorded"          accent={C.red} />
        <StatCard label="Collected"   value={fmtPKR(totalCollected)} accent={C.blue} />
      </div>

      <div style={S.card}>
        <div style={S.cardHead}>
          <span style={S.cardTitle}>
            Class Fee Status{classFilter && classFilter !== "all" ? ` — ${formatClassName(classFilter)}` : ""}
          </span>
          <span style={{ fontSize:"11px", color:"rgba(255,255,255,0.6)" }}>
            {classFilter === "all" ? "Tip: pick a Class above to focus on one class" : `${rows.length} students`}
          </span>
        </div>
        <div style={{ overflowX:"auto" }}>
          <table style={S.table}>
            <thead>
              <tr>
                {["#","Student", ...(showCampus ? ["Campus"] : []), "Roll No.","Section","Total Fee","Paid","Paid On","Status"].map(h =>
                  <th key={h} style={["Total Fee","Paid"].includes(h) ? { ...S.th, textAlign:"right" } : S.th}>{h}</th>
                )}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={showCampus ? 9 : 8} style={S.empty}>No students found for this class / session</td></tr>
              ) : rows.map(({ student: s, totalFee, totalPaid, status, lastPaid }, i) => (
                <tr key={s.id} style={{ background: i % 2 === 0 ? "#fff" : "#f8f9fc" }}>
                  <td style={{ ...S.td, color:"#8e92a0", fontWeight:600 }}>{i+1}</td>
                  <td style={{ ...S.td, fontWeight:700 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:"8px" }}>
                      <div style={{ width:28, height:28, borderRadius:"50%", background: status === "paid" ? C.green : status === "partial" ? C.amber : C.slateLight, color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:700, fontSize:11, flexShrink:0 }}>
                        {s.name.charAt(0).toUpperCase()}
                      </div>
                      {s.name}
                    </div>
                  </td>
                  {showCampus && <td style={S.td}><span style={S.badge("rgba(138,108,31,0.12)","#8a6c1f")}>{s.campus || "—"}</span></td>}
                  <td style={{ ...S.td, ...mono, fontWeight:600 }}>{s.rollNo}</td>
                  <td style={S.td}>{formatSection(s.section)}</td>
                  <td style={S.tdNum}>{fmtPKR(totalFee)}</td>
                  <td style={{ ...S.tdNum, color: totalPaid > 0 ? "#2e7d32" : "#8e92a0", fontWeight:700 }}>{fmtPKR(totalPaid)}</td>
                  <td style={{ ...S.td, ...mono, color: lastPaid ? C.text : C.slateLight }}>{lastPaid ? formatDate(lastPaid) : "—"}</td>
                  <td style={S.td}>
                    <span style={S.badge(
                      status === "paid" ? "rgba(46,125,50,0.12)" : status === "partial" ? "rgba(196,154,42,0.15)" : "rgba(196,30,36,0.1)",
                      status === "paid" ? "#2e7d32" : status === "partial" ? "#8a6c1f" : "#c41e24"
                    )}>
                      {status === "paid" ? "Paid" : status === "partial" ? "Partial" : "Not Paid"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ── Monthly Trend ─────────────────────────────────────────────────────────────
const MonthlyTrend = ({ flatAssignments, paymentHistory }) => {
  const [year, setYear] = useState(new Date().getFullYear());

  // FIX 6: dynamic years
  const years = useMemo(() => deriveYears(paymentHistory), [paymentHistory]);

  const data = useMemo(() => {
    const monthlyFee = flatAssignments.reduce((s, a) => s + (a.netPayable || 0), 0);
    return MONTHS.map((month, idx) => {
      const collected = paymentHistory.filter(p => {
        const d = new Date(p.date);
        return d.getFullYear() === year && d.getMonth() === idx;
      }).reduce((s, p) => s + (p.amount || 0), 0);
      const rate = monthlyFee > 0 ? Math.min((collected / monthlyFee) * 100, 100) : 0;
      return { month, expected: monthlyFee, collected, pending: Math.max(monthlyFee - collected, 0), rate };
    });
  }, [flatAssignments, paymentHistory, year]);

  return (
    <div>
      <div style={S.card}>
        <div style={S.filterRow}>
          <div style={S.fg}>
            <label style={S.label}>Year</label>
            <select style={S.select} value={year} onChange={e => setYear(parseInt(e.target.value))}>
              {years.map(y => <option key={y}>{y}</option>)}
            </select>
          </div>
        </div>
        <div style={{ padding:"20px", background:C.parchment }}>
          {data.map((d, i) => (
            <div key={i} style={{ marginBottom:"15px" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:"6px", flexWrap:"wrap", gap:"6px" }}>
                <span style={{ fontSize:"13px", fontWeight:"700", color:C.text, width:"90px" }}>{d.month}</span>
                <div style={{ display:"flex", gap:"16px", fontSize:"12px", alignItems:"baseline" }}>
                  <span style={{ color:C.green }}>Collected: <strong style={mono}>{fmtPKR(d.collected)}</strong></span>
                  <span style={{ color:C.slateLight }}>Expected: <span style={mono}>{fmtPKR(d.expected)}</span></span>
                  {d.pending > 0 && <span style={{ color:C.red }}>Pending: <strong style={mono}>{fmtPKR(d.pending)}</strong></span>}
                  <span style={{ ...mono, fontWeight:"700", color: d.rate >= 80 ? C.green : d.rate >= 50 ? C.amber : C.slateLight }}>{d.rate.toFixed(0)}%</span>
                </div>
              </div>
              <ProgressBar pct={d.rate} color={d.rate >= 80 ? C.green : d.rate >= 50 ? C.amber : C.slateLight} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ── Payment History (Student Detail) ──────────────────────────────────────────
const StudentPaymentHistoryView = ({ studentId, students, flatAssignments, paymentHistory, onBack }) => {
  const student = students.find(s => s.id === parseInt(studentId));
  const assignments = flatAssignments.filter(a => a.studentId === parseInt(studentId));
  const payments = paymentHistory.filter(p => Number(p.studentId) === parseInt(studentId));

  const totalFee  = assignments.reduce((s, a) => s + (a.netPayable || 0), 0);

  // FIX 1: Respect status on assignments for this student
  const historyPaid = payments.reduce((s, p) => s + (p.amount || 0), 0);
  const statusPaid  = assignments
    .filter(a => a.status === "Paid" || a.status === "Waived")
    .reduce((s, a) => s + (a.netPayable || 0), 0);
  const totalPaid = Math.max(historyPaid, statusPaid);

  // FIX 5: Include previous dues
  const previousDues = loadPreviousDues(parseInt(studentId));
  const balance = totalFee - totalPaid + previousDues;

  if (!student) return null;

  return (
    <div>
      <button onClick={onBack} style={{ ...S.btn("#f8f9fc","#1a1d23","1px solid #e8e9ed"), marginBottom:"16px" }}>
        <ArrowLeft /> 
      </button>

      <div style={{ ...S.card, marginBottom:"16px" }}>
        <div style={{ padding:"20px", display:"flex", alignItems:"center", gap:"16px" }}>
          <div style={{ width:48, height:48, borderRadius:"50%", background:"#0f1d36", color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"20px", fontWeight:"800", flexShrink:0 }}>
            {student.name.charAt(0).toUpperCase()}
          </div>
          <div>
            <div style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:"19px", fontWeight:"800", color:"#1a1d23" }}>{student.name}</div>
            <div style={{ fontSize:"12px", color:"#5a5e6a", marginTop:"3px", display:"flex", gap:"16px" }}>
              <span>Roll No: <strong>{student.rollNo}</strong></span>
              <span>Class: <strong>{formatClassName(student.class)}</strong></span>
              <span>Section: <strong>{formatSection(student.section)}</strong></span>
              {student.campus && <span>Campus: <strong>{student.campus}</strong></span>}
            </div>
          </div>
        </div>
      </div>

      <div style={S.statGrid}>
        <StatCard label="Total Fee Assigned" value={fmtPKR(totalFee)} accent="#2a4a7f" />
        <StatCard label="Total Paid" value={fmtPKR(totalPaid)} accent="#2e7d32" />
        <StatCard label="Previous Dues" value={fmtPKR(previousDues)} accent={previousDues > 0 ? "#8a6c1f" : "#8e92a0"} />
        <StatCard label="Balance Due" value={fmtPKR(balance)} accent={balance > 0 ? "#c41e24" : "#2e7d32"} />
      </div>

      {/* Assignment status breakdown */}
      <div style={S.card}>
        <div style={S.cardHead}><span style={S.cardTitle}>Fee Assignment Status</span></div>
        <div style={{ overflowX:"auto" }}>
          <table style={S.table}>
            <thead>
              <tr>
                {["Category","Month","Net Payable","Status"].map(h => <th key={h} style={S.th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {assignments.length === 0 ? (
                <tr><td colSpan="4" style={S.empty}>No fee assignments found</td></tr>
              ) : assignments.map((a, i) => (
                <tr key={a.entryKey || i} style={{ background: i % 2 === 0 ? "#fff" : "#f8f9fc" }}>
                  <td style={{ ...S.td, fontWeight:600 }}>{a.categoryLabel || a.categoryId}</td>
                  <td style={S.td}>{a.month || "—"}</td>
                  <td style={S.td}>{fmtPKR(a.netPayable)}</td>
                  <td style={S.td}>
                    <span style={S.badge(
                      a.status === "Paid" ? "rgba(46,125,50,0.12)" : a.status === "Waived" ? "rgba(196,154,42,0.15)" : "rgba(196,30,36,0.1)",
                      a.status === "Paid" ? "#2e7d32" : a.status === "Waived" ? "#8a6c1f" : "#c41e24"
                    )}>
                      {a.status || "Unpaid"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div style={S.card}>
        <div style={S.cardHead}><span style={S.cardTitle}>Payment History</span></div>
        <div style={{ overflowX:"auto" }}>
          <table style={S.table}>
            <thead>
              <tr>
                {["#","Date","Amount","Method","Category","Remarks"].map(h => <th key={h} style={S.th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {payments.length === 0 ? (
                <tr><td colSpan="6" style={S.empty}>No payments recorded for this student</td></tr>
              ) : payments.sort((a,b) => new Date(b.date) - new Date(a.date)).map((p, i) => (
                <tr key={p.id || i} style={{ background: i % 2 === 0 ? "#fff" : "#f8f9fc" }}>
                  <td style={{ ...S.td, color:"#8e92a0", fontWeight:600 }}>{i+1}</td>
                  <td style={S.td}>{formatDate(p.date)}</td>
                  <td style={{ ...S.td, color:"#2e7d32", fontWeight:700 }}>{fmtPKR(p.amount)}</td>
                  <td style={S.td}><span style={S.badge("rgba(30,58,95,0.08)","#1c3461")}>{p.method || "Cash"}</span></td>
                  <td style={S.td}>{p.feeCategory || "—"}</td>
                  <td style={{ ...S.td, color:"#5a5e6a", fontSize:"12px" }}>{p.remarks || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ── Main ReportTab ────────────────────────────────────────────────────────────
const ReportTab = ({ students: propStudents, feeAssignments: propFeeAssignments }) => {
  const [selectedReport, setSelectedReport] = useState("dashboard");
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [campusFilter, setCampusFilter] = useState("all");
  const [classFilter, setClassFilter] = useState("all");
  const [sessionFilter, setSessionFilter] = useState("all");

  // FIX 3: paymentHistory loaded reactively via useEffect, not stale useMemo
  const [paymentHistory, setPaymentHistory] = useState(() => {
    try {
      const ph = JSON.parse(localStorage.getItem("payment_history") || "[]");
      return Array.isArray(ph) ? ph : [];
    } catch { return []; }
  });

  // Refresh payment history whenever the tab becomes visible
  useEffect(() => {
    const refresh = () => {
      try {
        const ph = JSON.parse(localStorage.getItem("payment_history") || "[]");
        setPaymentHistory(Array.isArray(ph) ? ph : []);
      } catch { setPaymentHistory([]); }
    };
    window.addEventListener("focus", refresh);
    return () => window.removeEventListener("focus", refresh);
  }, []);

  // FIX 4: loadFromStorage memoized — only re-runs when props change
  const stored = useMemo(() => loadFromStorage(), [propStudents, propFeeAssignments]);

  const students = useMemo(() =>
    normalizeStudents(
      (propStudents && propStudents.length > 0) ? propStudents : (stored.students || [])
    ),
    [propStudents, stored.students]
  );

  const feeAssignments = useMemo(() =>
    (propFeeAssignments && Object.keys(propFeeAssignments).length > 0)
      ? propFeeAssignments
      : (stored.feeAssignments || {}),
    [propFeeAssignments, stored.feeAssignments]
  );

  const flatAssignments = useMemo(() => flattenAssignments(feeAssignments), [feeAssignments]);

  // FIX 7: unique campuses present in the roster — drives the new Campus filter
  const campuses = useMemo(() => ["all", ...deriveCampuses(students)], [students]);

  // Campus-wise scoping is only meaningful when more than one campus is present
  // in the data. Single-campus datasets skip the extra filter/column noise.
  const isMultiCampus = campuses.length > 2; // "all" + at least 2 real campuses

  // Derive unique classes from students
  const classes = useMemo(() => {
    const unique = [...new Set(students.map(s => s.class).filter(Boolean))].sort();
    return ["all", ...unique];
  }, [students]);

  // Derive unique academic sessions present on fee assignments
  const sessions = useMemo(
    () => ["all", ...deriveSessions(flatAssignments)],
    [flatAssignments]
  );

  // Students who have at least one fee assignment in the selected session
  const sessionStudentIds = useMemo(() => {
    if (sessionFilter === "all") return null;
    return new Set(
      flatAssignments
        .filter(a => a.session === sessionFilter)
        .map(a => a.studentId)
    );
  }, [flatAssignments, sessionFilter]);

  // Apply campus + class + session filters
  const filteredStudents = useMemo(() => {
    let result = students;
    if (campusFilter !== "all") result = result.filter(s => s.campus === campusFilter);
    if (classFilter !== "all") result = result.filter(s => s.class === classFilter);
    if (sessionFilter !== "all" && sessionStudentIds) {
      result = result.filter(s => sessionStudentIds.has(s.id));
    }
    return result;
  }, [students, campusFilter, classFilter, sessionFilter, sessionStudentIds]);

  const filteredAssignments = useMemo(() => {
    let result = flatAssignments;
    if (campusFilter !== "all") {
      result = result.filter(a => {
        const student = students.find(s => s.id === a.studentId);
        return student?.campus === campusFilter;
      });
    }
    if (classFilter !== "all") {
      result = result.filter(a => {
        const student = students.find(s => s.id === a.studentId);
        return student?.class === classFilter;
      });
    }
    if (sessionFilter !== "all") {
      result = result.filter(a => a.session === sessionFilter);
    }
    return result;
  }, [flatAssignments, students, campusFilter, classFilter, sessionFilter]);

  const filteredPayments = useMemo(() => {
    if (campusFilter === "all" && classFilter === "all" && sessionFilter === "all") return paymentHistory;
    const studentIds = new Set(filteredStudents.map(s => s.id));
    return paymentHistory.filter(p => studentIds.has(parseInt(p.studentId)));
  }, [paymentHistory, filteredStudents, campusFilter, classFilter, sessionFilter]);

  const reportTitle = REPORT_TYPES.find(t => t.id === selectedReport)?.label || "Fee Report";
  const sessionLabel = sessionFilter === "all" ? "All Sessions" : sessionFilter;

  // Campus shown on the letterhead: the admin's assigned campus by default
  // (from GET /api/campuses), overridden by the Campus filter above whenever
  // the person picks a specific campus to scope the report to.
  const adminCampusLabel = useAdminCampus();
  const campusLabel = campusFilter !== "all" ? campusFilter : (adminCampusLabel || (isMultiCampus ? "All Campuses" : ""));

  // FIX 6: dynamic years for CSV/print (used in export)
  const years = useMemo(() => deriveYears(paymentHistory), [paymentHistory]);

  const printableSummaryCards = useMemo(() => {
    const totalStudents = filteredStudents.length;
    const totalMonthlyReceivable = filteredAssignments.reduce((sum, a) => sum + (a.netPayable || 0), 0);
    const totalCollected = filteredStudents.reduce((sum, student) => {
      const summary = buildStudentSummary(student, filteredAssignments, filteredPayments);
      return sum + summary.totalPaid;
    }, 0);
    const totalConcessions = filteredAssignments.reduce((sum, a) => sum + (a.concessionAmount || 0), 0);
    return [
      { label: "Total Students", value: totalStudents },
      { label: "Monthly Receivable", value: fmtPKR(totalMonthlyReceivable) },
      { label: "Total Collected", value: fmtPKR(totalCollected) },
      { label: "Total Discounts", value: fmtPKR(totalConcessions) },
    ];
  }, [filteredStudents, filteredAssignments, filteredPayments]);

  // Column headers gain a "Campus" column whenever more than one campus is
  // in view (i.e. the person hasn't narrowed down to a single campus), so
  // multi-campus roles (superadmin/controller) can still tell rows apart.
  const showCampusColumn = isMultiCampus && campusFilter === "all";

  const tableHeaders = {
    dashboard:    ["#", "Month", "Expected", "Collected", "Pending", "Rate"],
    collection:   ["#", "Student", ...(showCampusColumn ? ["Campus"] : []), "Class", "Section", "Roll No.", "Total Fee", "Paid", "Balance", "Status"],
    classStatus:  ["#", "Student", ...(showCampusColumn ? ["Campus"] : []), "Roll No.", "Section", "Total Fee", "Paid", "Paid On", "Status"],
    pending:      ["#", "Student", ...(showCampusColumn ? ["Campus"] : []), "Class", "Section", "Roll No.", "Total Fee", "Paid", "Prev. Dues", "Outstanding"],
    concession:   ["#", "Student", ...(showCampusColumn ? ["Campus"] : []), "Class", "Fee Category", "Original", "Discount", "Net Payable"],
    classWise:    ["#", "Class", "Students", "Total Fee", "Collected", "Balance", "Discounts"],
    monthlyTrend: ["#", "Month", "Expected", "Collected", "Pending", "Rate"],
  }[selectedReport] || ["#", "Item", "Value"];

  const printableRows = useMemo(() => {
    if (selectedReport === "collection") {
      return filteredStudents.map((student, index) => {
        const item = buildStudentSummary(student, filteredAssignments, filteredPayments);
        return [index + 1, student.name, ...(showCampusColumn ? [student.campus || "—"] : []), formatClassName(student.class), formatSection(student.section), student.rollNo || "—", fmtPKR(item.totalFee), fmtPKR(item.totalPaid), fmtPKR(item.balance), item.status];
      });
    }
    if (selectedReport === "classStatus") {
      return filteredStudents
        .map(student => {
          const item = buildStudentSummary(student, filteredAssignments, filteredPayments);
          const pays = filteredPayments.filter(p => Number(p.studentId) === student.id).sort((a, b) => new Date(b.date) - new Date(a.date));
          const lastPaid = pays[0]?.date || item.assignments.find(a => (a.status === "Paid" || a.status === "Waived") && a.paymentDate)?.paymentDate || null;
          return { student, item, lastPaid };
        })
        .sort((a, b) => a.student.name.localeCompare(b.student.name))
        .map(({ student, item, lastPaid }, index) => [
          index + 1, student.name, ...(showCampusColumn ? [student.campus || "—"] : []), student.rollNo || "—", formatSection(student.section),
          fmtPKR(item.totalFee), fmtPKR(item.totalPaid),
          lastPaid ? formatDate(lastPaid) : "—",
          item.status === "paid" ? "Paid" : item.status === "partial" ? "Partial" : "Not Paid",
        ]);
    }
    if (selectedReport === "pending") {
      return filteredStudents
        .map(student => buildStudentSummary(student, filteredAssignments, filteredPayments))
        .filter(item => item.balance > 0)
        .sort((a, b) => b.balance - a.balance)
        .map((item, index) => [index + 1, item.student.name, ...(showCampusColumn ? [item.student.campus || "—"] : []), formatClassName(item.student.class), formatSection(item.student.section), item.student.rollNo || "—", fmtPKR(item.totalFee), fmtPKR(item.totalPaid), fmtPKR(item.previousDues), fmtPKR(item.balance)]);
    }
    if (selectedReport === "concession") {
      return filteredAssignments
        .filter(a => (a.concessionAmount || 0) > 0)
        .map((a, index) => {
          const student = filteredStudents.find(s => s.id === a.studentId);
          return student ? [index + 1, student.name, ...(showCampusColumn ? [student.campus || "—"] : []), formatClassName(student.class), a.categoryLabel || "—", fmtPKR(a.subtotal || 0), fmtPKR(a.concessionAmount || 0), fmtPKR(a.netPayable || 0)] : null;
        })
        .filter(Boolean);
    }
    if (selectedReport === "classWise") {
      const classMap = new Map();
      filteredStudents.forEach(student => {
        if (!classMap.has(student.class)) classMap.set(student.class, { className: student.class, students: 0, totalFee: 0, totalPaid: 0, concessions: 0 });
        const info = classMap.get(student.class);
        info.students += 1;
        const summary = buildStudentSummary(student, filteredAssignments, filteredPayments);
        info.totalFee += summary.totalFee;
        info.totalPaid += summary.totalPaid;
        info.concessions += filteredAssignments.filter(a => a.studentId === student.id).reduce((sum, a) => sum + (a.concessionAmount || 0), 0);
      });
      return Array.from(classMap.values()).map((item, index) => [index + 1, item.className || "—", item.students, fmtPKR(item.totalFee), fmtPKR(item.totalPaid), fmtPKR(item.totalFee - item.totalPaid), fmtPKR(item.concessions)]);
    }
    return MONTHS.map((month, index) => {
      const collected = filteredPayments.filter(p => new Date(p.date).getMonth() === index).reduce((sum, p) => sum + (p.amount || 0), 0);
      const expected = filteredAssignments.reduce((sum, a) => sum + (a.netPayable || 0), 0);
      const pending = Math.max(expected - collected, 0);
      const rate = expected > 0 ? Math.min((collected / expected) * 100, 100).toFixed(1) : 0;
      return [index + 1, month, fmtPKR(expected), fmtPKR(collected), fmtPKR(pending), `${rate}%`];
    });
  }, [filteredStudents, filteredAssignments, filteredPayments, selectedReport, showCampusColumn]);

  const exportCsv = () => {
    const rows = [];
    const summaryRows = [
      ["School", SCHOOL_NAME],
      ["Campus", campusLabel || "All Campuses"],
      ["Generated On", getGeneratedTimestamp()],
      [],
      ["Report Type", REPORT_TYPES.find(t => t.id === selectedReport)?.label || selectedReport],
      ["Campus Filter", campusFilter === "all" ? "All Campuses" : campusFilter],
      ["Class Filter", classFilter === "all" ? "All Classes" : formatClassName(classFilter)],
      ["Session Filter", sessionFilter === "all" ? "All Sessions" : sessionFilter],
      ["Students Shown", filteredStudents.length],
      ["Fee Assignments", filteredAssignments.length],
    ];

    if (selectedReport === "dashboard") {
      const totalStudents = filteredStudents.length;
      const totalMonthlyReceivable = filteredAssignments.reduce((sum, a) => sum + (a.netPayable || 0), 0);
      const totalCollected = filteredStudents.reduce((sum, s) => sum + buildStudentSummary(s, filteredAssignments, filteredPayments).totalPaid, 0);
      const totalConcessions = filteredAssignments.reduce((sum, a) => sum + (a.concessionAmount || 0), 0);
      rows.push(...summaryRows, [], ["Metric", "Value"], ["Total Students", totalStudents], ["Monthly Receivable", rawNum(totalMonthlyReceivable)], ["Total Collected", rawNum(totalCollected)], ["Total Discounts", rawNum(totalConcessions)]);
    } else if (selectedReport === "collection") {
      rows.push(...summaryRows, [], ["Student Name", ...(showCampusColumn ? ["Campus"] : []), "Class", "Section", "Roll No.", "Total Fee", "Paid", "Balance", "Status"]);
      filteredStudents.forEach(student => {
        const item = buildStudentSummary(student, filteredAssignments, filteredPayments);
        rows.push([student.name, ...(showCampusColumn ? [student.campus || ""] : []), formatClassName(student.class), formatSection(student.section), student.rollNo || "", rawNum(item.totalFee), rawNum(item.totalPaid), rawNum(item.balance), item.status]);
      });
    } else if (selectedReport === "classStatus") {
      rows.push(...summaryRows, [], ["Student Name", ...(showCampusColumn ? ["Campus"] : []), "Roll No.", "Section", "Total Fee", "Paid", "Paid On", "Status"]);
      filteredStudents
        .map(student => {
          const item = buildStudentSummary(student, filteredAssignments, filteredPayments);
          const pays = filteredPayments.filter(p => Number(p.studentId) === student.id).sort((a, b) => new Date(b.date) - new Date(a.date));
          const lastPaid = pays[0]?.date || item.assignments.find(a => (a.status === "Paid" || a.status === "Waived") && a.paymentDate)?.paymentDate || null;
          return { student, item, lastPaid };
        })
        .sort((a, b) => a.student.name.localeCompare(b.student.name))
        .forEach(({ student, item, lastPaid }) => rows.push([
          student.name, ...(showCampusColumn ? [student.campus || ""] : []), student.rollNo || "", formatSection(student.section),
          rawNum(item.totalFee), rawNum(item.totalPaid),
          lastPaid ? formatDate(lastPaid) : "Not Paid",
          item.status === "paid" ? "Paid" : item.status === "partial" ? "Partial" : "Not Paid",
        ]));
    } else if (selectedReport === "pending") {
      rows.push(...summaryRows, [], ["Student Name", ...(showCampusColumn ? ["Campus"] : []), "Class", "Section", "Roll No.", "Total Fee", "Paid", "Prev. Dues", "Outstanding"]);
      filteredStudents
        .map(student => buildStudentSummary(student, filteredAssignments, filteredPayments))
        .filter(item => item.balance > 0)
        .sort((a, b) => b.balance - a.balance)
        .forEach(item => rows.push([item.student.name, ...(showCampusColumn ? [item.student.campus || ""] : []), formatClassName(item.student.class), formatSection(item.student.section), item.student.rollNo || "", rawNum(item.totalFee), rawNum(item.totalPaid), rawNum(item.previousDues), rawNum(item.balance)]));
    } else if (selectedReport === "concession") {
      rows.push(...summaryRows, [], ["Student Name", ...(showCampusColumn ? ["Campus"] : []), "Class", "Fee Category", "Original Amount", "Discount", "Net Payable"]);
      filteredAssignments.filter(a => (a.concessionAmount || 0) > 0).forEach(a => {
        const student = filteredStudents.find(s => s.id === a.studentId);
        if (student) rows.push([student.name, ...(showCampusColumn ? [student.campus || ""] : []), formatClassName(student.class), a.categoryLabel || "", rawNum(a.subtotal || 0), rawNum(a.concessionAmount || 0), rawNum(a.netPayable || 0)]);
      });
    } else if (selectedReport === "classWise") {
      const classMap = new Map();
      filteredStudents.forEach(student => {
        if (!classMap.has(student.class)) classMap.set(student.class, { className: student.class, students: 0, totalFee: 0, totalPaid: 0, concessions: 0 });
        const info = classMap.get(student.class);
        info.students += 1;
        const summary = buildStudentSummary(student, filteredAssignments, filteredPayments);
        info.totalFee += summary.totalFee;
        info.totalPaid += summary.totalPaid;
        info.concessions += filteredAssignments.filter(a => a.studentId === student.id).reduce((sum, a) => sum + (a.concessionAmount || 0), 0);
      });
      rows.push(...summaryRows, [], ["Class", "Students", "Total Fee", "Collected", "Balance", "Discounts"]);
      Array.from(classMap.values()).forEach(item => rows.push([formatClassName(item.className), item.students, rawNum(item.totalFee), rawNum(item.totalPaid), rawNum(item.totalFee - item.totalPaid), rawNum(item.concessions)]));
    } else {
      rows.push(...summaryRows, [], ["Month", "Expected", "Collected", "Pending", "Collection Rate"]);
      MONTHS.forEach((month, idx) => {
        const collected = filteredPayments.filter(p => new Date(p.date).getMonth() === idx).reduce((sum, p) => sum + (p.amount || 0), 0);
        const expected = filteredAssignments.reduce((sum, a) => sum + (a.netPayable || 0), 0);
        const pending = Math.max(expected - collected, 0);
        const rate = expected > 0 ? Math.min((collected / expected) * 100, 100).toFixed(1) : 0;
        rows.push([month, rawNum(expected), rawNum(collected), rawNum(pending), `${rate}%`]);
      });
    }

    const csv = rows
      .map(row => row.map(value => `"${formatCsvValue(value)}"`).join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `fee-report-${selectedReport}-${campusFilter === "all" ? "all-campuses" : campusFilter.toLowerCase().replace(/\s+/g, "-")}-${classFilter === "all" ? "all-classes" : classFilter.toLowerCase().replace(/\s+/g, "-")}-${sessionFilter === "all" ? "all-sessions" : sessionFilter}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Print to a clean, self-contained window (the same reliable pattern the
  // challans use) so the PDF always has content and a proper letterhead —
  // instead of fighting the dashboard chrome with @media print rules.
  const printReport = () => {
    const headers = tableHeaders;
    const rows = printableRows;
    const cards = printableSummaryCards;
    const classLabelPrint = classFilter === "all" ? "All Classes" : formatClassName(classFilter);
    const logoUrl = `${window.location.origin}${SCHOOL_LOGO}`;
    const campusHtml = campusLabel ? `<div class="campus">${campusLabel.replace(/&/g, "&amp;").replace(/</g, "&lt;")}</div>` : "";
    const footStamp = `${SCHOOL_NAME}${campusLabel ? " — " + campusLabel : ""}`;

    const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const RIGHT = new Set(["Expected", "Collected", "Pending", "Rate", "Total Fee", "Paid", "Balance", "Prev. Dues", "Outstanding", "Original", "Discount", "Net Payable", "Students", "Discounts"]);
    const STATUS_COLOR = { Paid: "#2e7d32", Partial: "#8a6c1f", Unpaid: "#c41e24", "Not Paid": "#c41e24", Pending: "#c41e24", Waived: "#7c3aed", paid: "#2e7d32", partial: "#8a6c1f", pending: "#c41e24" };

    const headHtml = headers.map(h => `<th class="${RIGHT.has(h) ? "r" : ""}">${esc(h)}</th>`).join("");
    const bodyHtml = rows.length
      ? rows.map(row => `<tr>${row.map((cell, ci) => {
          const h = headers[ci];
          const isMoney = RIGHT.has(h) || /^(PKR|Rs)/.test(String(cell));
          const sc = STATUS_COLOR[cell];
          const cls = `${RIGHT.has(h) ? "r " : ""}${isMoney ? "num" : ""}`.trim();
          const style = sc ? ` style="color:${sc};font-weight:700"` : "";
          return `<td class="${cls}"${style}>${esc(cell)}</td>`;
        }).join("")}</tr>`).join("")
      : `<tr><td class="empty" colspan="${headers.length}">No data available for this report.</td></tr>`;

    const cardsHtml = cards.map(c => `<div class="card"><div class="cv">${esc(c.value)}</div><div class="cl">${esc(c.label)}</div></div>`).join("");

    const doc = `<!DOCTYPE html><html><head><meta charset="utf-8" />
<title>${esc(reportTitle)} — ${esc(SCHOOL_NAME)}</title>
<link rel="preconnect" href="https://fonts.googleapis.com"><link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700;800&family=Inter:wght@400;500;600;700&family=IBM+Plex+Mono:wght@500;600;700&display=swap" rel="stylesheet">
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:'Inter','Segoe UI',sans-serif;color:#1a1d23;padding:28px 30px;font-size:12px}
  .mono{font-family:'IBM Plex Mono',monospace;font-variant-numeric:tabular-nums}
  .lh{display:flex;align-items:center;gap:18px;border-bottom:3px solid #0f1d36;padding-bottom:14px;position:relative}
  .lh::after{content:"";position:absolute;left:0;right:0;bottom:-3px;height:3px;background:#8a6c1f}
  .lh img{height:60px;width:auto}
  .lh .school{font-family:'Playfair Display',Georgia,serif;font-size:26px;font-weight:800;color:#0f1d36;line-height:1.1}
  .lh .campus{font-size:12px;font-weight:700;color:#8a6c1f;text-transform:uppercase;letter-spacing:2px;margin-top:3px}
  .lh .sub{font-size:11px;color:#5a5e6a;margin-top:3px}
  .meta{display:flex;justify-content:space-between;align-items:baseline;margin:16px 0 6px}
  .meta .title{font-family:'Playfair Display',Georgia,serif;font-size:16px;font-weight:700;color:#1a1d23}
  .meta .gen{font-size:11px;color:#5a5e6a}
  .scope{font-size:11.5px;color:#5a5e6a;margin-bottom:14px}
  .scope b{color:#1a1d23}
  .cards{display:flex;gap:10px;margin-bottom:16px}
  .card{flex:1;border:1px solid #ddd3bd;border-top:3px solid #0f1d36;border-radius:8px;padding:10px 12px;background:#fbf8f2}
  .card .cv{font-family:'IBM Plex Mono',monospace;font-variant-numeric:tabular-nums;font-size:15px;font-weight:700;color:#1a1d23}
  .card .cl{font-size:10px;color:#5a5e6a;text-transform:uppercase;letter-spacing:0.5px;margin-top:3px}
  table{width:100%;border-collapse:collapse;font-size:11px}
  thead th{background:#0f1d36;color:#fff;text-align:left;padding:9px 10px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;border-bottom:3px solid #8a6c1f}
  thead th.r{text-align:right}
  tbody td{padding:8px 10px;border-bottom:1px solid #e2ddcd;color:#1a1d23}
  tbody td.r,tbody td.num{text-align:right}
  tbody td.num{font-family:'IBM Plex Mono',monospace;font-variant-numeric:tabular-nums}
  tbody tr:nth-child(even) td{background:#fbf8f2}
  td.empty{text-align:center;padding:30px;color:#8e92a0;font-style:italic}
  .foot{margin-top:26px;display:flex;justify-content:space-between;align-items:flex-end}
  .sign{font-size:11px;color:#5a5e6a}
  .sign .line{border-top:1px solid #1a1d23;width:170px;margin-top:34px;padding-top:4px}
  .stamp{font-size:9.5px;color:#8e92a0;text-align:right}
  @media print{
    @page{size:A4 portrait;margin:14mm 12mm; @bottom-right{content:"Page " counter(page) " of " counter(pages);font-size:9px;color:#8e92a0}}
    body{-webkit-print-color-adjust:exact;print-color-adjust:exact;padding:0}
    thead{display:table-header-group}      /* repeat the column header on every printed page */
    tfoot{display:table-footer-group}
    tr{page-break-inside:avoid}            /* never split a row across two pages */
    .lh,.cards,.foot{page-break-inside:avoid}
    .foot{margin-top:18px}
  }
</style></head>
<body>
  <div class="lh">
    <img src="${logoUrl}" alt="School logo" onerror="this.style.display='none'" />
    <div>
      <div class="school">${esc(SCHOOL_NAME)}</div>
      ${campusHtml}
      <div class="sub">Fee Management Report</div>
    </div>
  </div>
  <div class="meta">
    <div class="title">${esc(reportTitle)}</div>
    <div class="gen">Generated on: ${esc(getGeneratedTimestamp())}</div>
  </div>
  <div class="scope">Class: <b>${esc(classLabelPrint)}</b> &nbsp;·&nbsp; Academic Session: <b>${esc(sessionLabel)}</b> &nbsp;·&nbsp; Students shown: <b>${esc(filteredStudents.length)}</b></div>
  <div class="cards">${cardsHtml}</div>
  <table><thead><tr>${headHtml}</tr></thead><tbody>${bodyHtml}</tbody></table>
  <div class="foot">
    <div class="sign"><div class="line">Prepared by</div></div>
    <div class="sign"><div class="line">Verified by (Principal)</div></div>
    <div class="stamp">Computer-generated report<br/>${esc(footStamp)}</div>
  </div>
  <script>
    (function(){ function go(){ window.focus(); window.print(); }
      if (document.readyState === 'complete') setTimeout(go, 350);
      else window.addEventListener('load', function(){ setTimeout(go, 350); }); })();
  </script>
</body></html>`;

    const win = window.open("", "_blank");
    if (!win) { alertDialog("Please allow pop-ups for this site to print the report.", { title: "Pop-up Blocked" }); return; }
    win.document.write(doc);
    win.document.close();
  };

  const renderContent = () => {
    if (selectedStudentId) {
      return (
        <StudentPaymentHistoryView
          studentId={selectedStudentId}
          students={students}
          flatAssignments={flatAssignments}
          paymentHistory={paymentHistory}
          onBack={() => setSelectedStudentId(null)}
        />
      );
    }
    const props = { students: filteredStudents, flatAssignments: filteredAssignments, paymentHistory: filteredPayments };
    switch (selectedReport) {
      case "dashboard":    return <DashboardOverview {...props} />;
      case "collection":   return <CollectionReport {...props} showCampus={showCampusColumn} />;
      case "classStatus":  return <ClassFeeStatusReport {...props} classFilter={classFilter} showCampus={showCampusColumn} />;
      case "pending":      return <PendingFeeReport {...props} onStudentClick={setSelectedStudentId} showCampus={showCampusColumn} />;
      case "concession":   return <ConcessionReport students={filteredStudents} flatAssignments={filteredAssignments} showCampus={showCampusColumn} />;
      case "classWise":    return <ClassWiseAnalysis {...props} />;
      case "monthlyTrend": return <MonthlyTrend flatAssignments={filteredAssignments} paymentHistory={filteredPayments} />;
      default:             return <DashboardOverview {...props} />;
    }
  };

  return (
    <>
      <div style={S.wrap} className="report-shell">
        {/* Report selector + stats header */}
        <div style={S.card} className="report-card no-print">
          <div style={S.cardHead}>
            <span style={S.cardTitle}>Fee Management Reports</span>
            <span style={{ fontSize:"12px", color:"rgba(255,255,255,0.7)", background:"rgba(255,255,255,0.12)", padding:"3px 10px", borderRadius:"20px", fontWeight:600 }}>
              {students.length} students · {flatAssignments.length} fee assignments
            </span>
          </div>

          {!selectedStudentId && (
            <>
              <ReportTypeTabs value={selectedReport} onChange={setSelectedReport} />
              <div style={S.filterRow}>
                {isMultiCampus && (
                  <div style={S.fg}>
                    <label style={S.label}>Campus</label>
                    <select style={S.select} value={campusFilter} onChange={e => { setCampusFilter(e.target.value); setSelectedStudentId(null); }}>
                      {campuses.map(c => (
                        <option key={c} value={c}>{c === "all" ? "All Campuses" : c}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div style={S.fg}>
                  <label style={S.label}>Class</label>
                  <select style={S.select} value={classFilter} onChange={e => { setClassFilter(e.target.value); setSelectedStudentId(null); }}>
                    {classes.map(c => (
                      <option key={c} value={c}>{c === "all" ? "All Classes" : formatClassName(c)}</option>
                    ))}
                  </select>
                </div>
                <div style={S.fg}>
                  <label style={S.label}>Academic Session</label>
                  <select style={S.select} value={sessionFilter} onChange={e => { setSessionFilter(e.target.value); setSelectedStudentId(null); }}>
                    {sessions.map(s => (
                      <option key={s} value={s}>{s === "all" ? "All Sessions" : s}</option>
                    ))}
                  </select>
                </div>
                {(campusFilter !== "all" || classFilter !== "all" || sessionFilter !== "all") && (
                  <div style={{ display:"flex", alignItems:"flex-end" }}>
                    <span style={{ fontSize:"12px", color:C.slate, padding:"9px 12px", background:C.white, borderRadius:"8px", border:`1px solid ${C.ledgerLine}` }}>
                      Showing <strong style={{ ...mono, color:C.text }}>{filteredStudents.length}</strong> of <span style={mono}>{students.length}</span> students
                    </span>
                  </div>
                )}
                <div style={{ marginLeft:"auto", display:"flex", gap:"8px", alignItems:"flex-end" }} className="no-print">
                  <button onClick={exportCsv} style={S.btn(C.white, C.text, `1px solid ${C.ledgerLine}`)}>
                    <Ico.download size={15} /> Export CSV
                  </button>
                  <button onClick={printReport} style={S.btn(C.navy, "#fff")}>
                    <Ico.printer size={15} /> Print Report
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        <div>{renderContent()}</div>
      </div>
    </>
  );
};

export default ReportTab;